import React, { FC, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import videojs from 'video.js'
import { QualityLevel } from 'videojs-contrib-quality-levels'
import styles from './video-quality.module.scss'

export const initialVideoBandwidth = () => {
  const connection = (navigator as Navigator & { connection?: { downlink?: number } }).connection
  const downlink = connection?.downlink
  return downlink && Number.isFinite(downlink) && downlink > 0 ? downlink * 1000000 : 10000000
}

const qualityLabel = (level: QualityLevel) =>
  level.height ? `${level.height}p` : `${Math.round(level.bitrate / 1000)} kbps`

const VideoQuality: FC<{ player: videojs.Player | null }> = ({ player }) => {
  const [host, setHost] = useState<HTMLElement | null>(null)
  const [levels, setLevels] = useState<QualityLevel[]>([])
  const [selected, setSelected] = useState('auto')
  const [height, setHeight] = useState(0)
  const [open, setOpen] = useState(false)
  const control = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!player) return
    const bar = player.getChild('controlBar')
    if (!bar) return
    const element = document.createElement('div')
    element.className = `vjs-control ${styles.qualityControl}`
    bar.el().insertBefore(element, bar.getChild('fullscreenToggle')?.el() || null)
    setHost(element)
    const qualityLevels = player.qualityLevels()
    const updateLevels = () => setLevels(Array.from(qualityLevels))
    const updateHeight = () => setHeight(player.videoHeight() || 0)
    const reset = () => {
      setSelected('auto')
      setHeight(0)
      setOpen(false)
      updateLevels()
    }
    updateLevels()
    qualityLevels.on(['addqualitylevel', 'removequalitylevel'], updateLevels)
    // Playlist changes can precede decoded frames; read actual video dimensions instead.
    player.on(['loadeddata', 'resize', 'playing'], updateHeight)
    player.on('loadstart', reset)
    return () => {
      qualityLevels.off(['addqualitylevel', 'removequalitylevel'], updateLevels)
      player.off(['loadeddata', 'resize', 'playing'], updateHeight)
      player.off('loadstart', reset)
      element.remove()
    }
  }, [player])

  useEffect(() => {
    if (!open) return
    const closeOutside = (event: MouseEvent) => {
      if (!control.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', closeOutside)
    return () => document.removeEventListener('mousedown', closeOutside)
  }, [open])

  if (!host || !player || levels.length < 2) return null
  const actual = height ? `${height}p` : ''
  const autoLabel = `Авто${actual ? ` · ${actual}` : ''}`
  const selectedLevel = levels.find((level) => level.id === selected)
  const label = selected === 'auto' || !selectedLevel ? autoLabel : qualityLabel(selectedLevel)
  const choose = (id: string) => {
    // Enable the target before disabling others so VHS never sees all levels disabled.
    levels.filter((level) => id === 'auto' || level.id === id).forEach((level) => (level.enabled = true))
    if (id !== 'auto') levels.filter((level) => level.id !== id).forEach((level) => (level.enabled = false))
    setSelected(id)
    setOpen(false)
  }
  return createPortal(
    <div
      ref={control}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation()
          setOpen(false)
          control.current?.querySelector('button')?.focus()
        }
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false)
      }}
    >
      <button
        type="button"
        className={`vjs-icon-cog ${styles.qualityButton}`}
        aria-label={`Качество: ${label}`}
        aria-expanded={open}
        title={`Качество: ${label}`}
        onClick={() => setOpen(!open)}
      />
      {open && (
        <div className={styles.menu} role="group" aria-label="Качество видео">
          {[
            { id: 'auto', label: autoLabel },
            ...levels
              .slice()
              .sort((a, b) => (b.height || 0) - (a.height || 0))
              .map((level) => ({
                id: level.id,
                label: qualityLabel(level),
              })),
          ].map((option) => (
            <button
              type="button"
              key={option.id}
              aria-pressed={selected === option.id}
              onClick={() => choose(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>,
    host,
  )
}

export default VideoQuality
